import { useEffect, useState, useCallback, useRef } from "react";
import { MdSearch, MdDownload, MdAddBox } from "react-icons/md";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import { getScanners, deleteScanner, forceLogout } from "api/scanner.api"; // API endpoints
import ScannerTable from "components/scanner/ScannerTable";
import { InputField } from "components/FormFields";
import CustomButton from "components/CustomButton";
import SideDrawerModal from "components/SideDrawerModal";
import ViewScannerDetails from "components/scanner/ViewScannerDetails";
import LogsTable from "components/scanner/LogsTable";
import Modal from "components/common/Modal";
import { MdLogout } from "react-icons/md";

export default function Scanners() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );
  const [selectedScanner, setSelectedScanner] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [buttonType, setButtonType] = useState("");
  const [ismodelOpen, setIsmodelOpen] = useState(false);

  const handleOpenView = (scannerItem) => {
    setButtonType("View");
    setSelectedScanner(scannerItem);
    setIsViewOpen(true);
  };

  const handleViewLoginLog = (scannerItem) => {
    try {
      setButtonType("login");
      setSelectedScanner(scannerItem);
      setIsViewOpen(true);
    } catch (error) {
      console.error("Failed to fetch login logs:", error);
    }
  };
    useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };

    window.addEventListener(
      "companyChanged",
      handleOrganizerChange
    );

    return () =>
      window.removeEventListener(
        "companyChanged",
        handleOrganizerChange
      );
         
  }, []);
  const handleViewScannerLog = (scannerItem) => {
    try {
      setButtonType("scan");
      setSelectedScanner(scannerItem);
      setIsViewOpen(true);
    } catch (error) {
      console.error("Failed to fetch login logs:", error);
    }
  };

  const handleLogout = (scannerItem) => {
    try {
      setSelectedScanner(scannerItem);
      setIsmodelOpen(true);
    } catch (error) {
      console.error("Failed to fetch login logs:", error);
    }
  };

  const [paginationInfo, setPaginationInfo] = useState({
    totalScanners: 0,
    totalPages: 1,
  });

  const abortControllerRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const res = await getScanners({
        company_id: selectedOrganizer,
        page,
        limit,
        search: debouncedSearch,
      });

      const payload = res.data?.data || res.data || {};
      const list = payload.scanners || (Array.isArray(payload) ? payload : []);

      setData(list);
      setPaginationInfo({
        totalScanners: payload.pagination?.totalScanners || list.length,
        totalPages: payload.pagination?.totalPages || 1,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to fetch scanners:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch,selectedOrganizer]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(data.map((item, idx) => item._id || idx));
    } else {
      setSelectedRows([]);
    }
  };

  const onConfirm = async () => {
    if (!selectedScanner?._id) return;

    try {
      const res = await forceLogout(selectedScanner._id);

      if (res?.data) {
        setIsmodelOpen(false);
        setSelectedScanner(null);
      }
    } catch (error) {
      console.error("Force logout failed:", error);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleExportCSV = () => {
    const exportData =
      selectedRows.length > 0
        ? data.filter((item, idx) => selectedRows.includes(item._id || idx))
        : data;

    if (!exportData.length) return;

    const headers = [
      "Name",
      "Email",
      "Phone",
      "CNP",
      "Login Code",
      "Country",
      "Gate",
      "Status",
    ];
    const safeStr = (val) => String(val || "").replace(/"/g, '""');

    const rows = exportData.map((c) => [
      `"${safeStr(
        c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim()
      )}"`,
      `"${safeStr(c.email)}"`,
      `"${safeStr(c.phone || c.mobile)}"`,
      `"${safeStr(c.cnp)}"`,
      `"${safeStr(c.loginCode || c.code)}"`,
      `"${safeStr(c.country)}"`,
      `"${safeStr(c.gate || c.blockNo)}"`,
      `"${c.status || (c.isActive ? "Active" : "Inactive")}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `scanners_${
        selectedRows.length > 0 ? "selected" : "all"
      }_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TablePageLayout>
      <div className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col">
          <InputField
            label="SEARCH"
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            icon={<MdSearch className="text-lg text-gray-400" />}
          />
        </div>

        <div className="hidden lg:block"></div>
        <div className="hidden lg:block"></div>

        <div className="flex items-center justify-start gap-2 sm:justify-end lg:justify-end">
          <CustomButton
            label={
              selectedRows.length > 0
                ? `Export Selected (${selectedRows.length})`
                : "Export CSV"
            }
            icon={MdDownload}
            iconPos="left"
            variant="outline"
            color="#1A1A1A"
            borderColor="#02a9e0"
            disabled={data.length === 0}
            onClick={handleExportCSV}
          />

          <CustomButton
            label="Add Scanner"
            icon={MdAddBox}
            iconPos="right"
            variant="solid"
            color="#02a9e0"
            onClick={() => (window.location.href = "/admin/scanners/create")}
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading scanners...
        </div>
      ) : (
        <div className="table-card overflow-hidden">
          <ScannerTable
            data={data}
            onRefresh={fetchData}
            onDelete={deleteScanner}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            viewDetails={handleOpenView}
            loginLogs={handleViewLoginLog}
            scanLogs={handleViewScannerLog}
            logout={handleLogout}
          />

          <Pagination
            currentPage={page}
            totalPages={paginationInfo.totalPages}
            totalEntries={paginationInfo.totalScanners}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}
      <SideDrawerModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={`${`${selectedScanner?.firstName || ""} ${
          selectedScanner?.lastName || ""
        }`.trim()} - ${
          buttonType === "View"
            ? "Scanner Details"
            : buttonType === "login"
            ? "Log History"
            : "Scan History"
        }`}
        width="520px"
      >
        {buttonType === "View" ? (
          <ViewScannerDetails data={selectedScanner} />
        ) : (
          <LogsTable
            companyId={selectedOrganizer}
            scannerId={selectedScanner?._id}
            type={buttonType}
          />
        )}
      </SideDrawerModal>
      <Modal open={ismodelOpen} showClose={false} maxWidth="max-w-md">
        <div className="flex flex-col items-center py-3 text-center">
          {/* Red Circle Icon */}
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-red-500">
            <MdLogout size={36} />
          </div>

          {/* Text */}
          <h2 className="logout-confirm-title mb-2">Are you sure?</h2>
          <p className="logout-confirm-desc mb-8">
            You are about to log out this user. This action will end their
            current session immediately.
          </p>

          {/* Action Buttons */}
          <div className="flex w-full gap-3">
            <button
              type="button"
              onClick={() => setIsmodelOpen(false)}
              style={{
                borderRadius: "10.7px",
                border: "1.34px solid #E2E8F0",
                background: "#FFFFFF",
                opacity: 1,
                color: "#1a1a1a",
              }}
              className="w-1/2 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              style={{
                borderRadius: "10.7px",
                background: "#EF4444",
                opacity: 1,
                color: "white",
              }}
              className="w-1/2  py-2"
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </Modal>
    </TablePageLayout>
  );
}
